import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Layout} from "../../pages/siren/Layout";
import {platformAPI} from "../../api/axiosConfig";
import {BlueButton} from "../../components/ui/Buttons";
import {Client} from "../../types/userManagement";
import {SelectList, SelectListHandle} from "../../components/select-list/SelectList";
import {useSiren} from "../../pages/siren/SirenContext";

type DriverMode = "all" | "client_only" | "disabled";

type SirenClientDriverConfig = {
    id?: string;
    clientID: string;
    callMode: DriverMode;
    emailMode: DriverMode;
    smsMode: DriverMode;
    appMode: DriverMode;
};

const MODE_OPTIONS: {value: DriverMode; label: string; description: string}[] = [
    {
        value: "all",
        label: "Global + client drivers",
        description: "Use any available driver, including global and client-scoped drivers",
    },
    {
        value: "client_only",
        label: "Client drivers only",
        description: "Restrict to drivers that are scoped specifically to this client",
    },
    {
        value: "disabled",
        label: "Disabled",
        description: "This delivery type is not permitted for this client",
    },
];

function ModeDot({mode}: {mode: DriverMode}) {
    const cls = mode === "all" ? "bg-green" : mode === "client_only" ? "bg-dark-orange" : "bg-red";
    return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${cls}`}/>;
}

function ConfigDots({callMode, emailMode, smsMode, appMode}: {callMode: DriverMode; emailMode: DriverMode; smsMode: DriverMode; appMode: DriverMode}) {
    return (
        <span className="flex items-center gap-1 shrink-0" title={`Call: ${callMode} · Email: ${emailMode} · SMS: ${smsMode} · App: ${appMode}`}>
            <ModeDot mode={callMode}/>
            <ModeDot mode={emailMode}/>
            <ModeDot mode={smsMode}/>
            <ModeDot mode={appMode}/>
        </span>
    );
}

function ModeSelector({label, value, onChange}: {label: string; value: DriverMode; onChange: (v: DriverMode) => void}) {
    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">{label}</p>
            <div className="flex flex-col gap-2">
                {MODE_OPTIONS.map(opt => (
                    <label
                        key={opt.value}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            value === opt.value
                                ? "border-blue bg-light-light-grey"
                                : "border-light-grey hover:bg-light-light-grey"
                        }`}
                    >
                        <input
                            type="radio"
                            className="mt-0.5 shrink-0"
                            checked={value === opt.value}
                            onChange={() => onChange(opt.value)}
                        />
                        <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">{opt.label}</span>
                            <span className="text-xs text-light-grey">{opt.description}</span>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
}

function ConfigPanel({client, onSaved}: {client: Client; onSaved: () => void}) {
    const [callMode, setCallMode] = useState<DriverMode>("disabled");
    const [emailMode, setEmailMode] = useState<DriverMode>("disabled");
    const [smsMode, setSmsMode] = useState<DriverMode>("disabled");
    const [appMode, setAppMode] = useState<DriverMode>("disabled");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setLoading(true);
        setSaved(false);
        setError(null);
        platformAPI.get<SirenClientDriverConfig>(`api/siren/client-driver-configs/${client.id}`)
            .then(res => {
                setCallMode(res.data.callMode);
                setEmailMode(res.data.emailMode);
                setSmsMode(res.data.smsMode);
                setAppMode(res.data.appMode ?? "disabled");
            })
            .catch(() => {
                setCallMode("disabled");
                setEmailMode("disabled");
                setSmsMode("disabled");
                setAppMode("disabled");
            })
            .finally(() => setLoading(false));
    }, [client.id]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            await platformAPI.put(`api/siren/client-driver-configs/${client.id}`, {callMode, emailMode, smsMode, appMode});
            setSaved(true);
            onSaved();
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Error saving configuration");
        } finally {
            setSaving(false);
        }
    }, [client.id, callMode, emailMode, smsMode, appMode, onSaved]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className="flex flex-col gap-4 p-4">
            <div>
                <p className="font-semibold text-sm">{client.name}</p>
                <p className="text-xs text-light-grey">Configure which drivers are available for each delivery type.</p>
            </div>
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-5">
                <ModeSelector label="Call" value={callMode} onChange={setCallMode}/>
                <ModeSelector label="Email" value={emailMode} onChange={setEmailMode}/>
                <ModeSelector label="SMS" value={smsMode} onChange={setSmsMode}/>
                <ModeSelector label="App" value={appMode} onChange={setAppMode}/>
            </div>
            {error && <p className="text-sm text-red">{error}</p>}
            {saved && <p className="text-sm text-dark-green">Saved</p>}
            <div className="flex justify-end pb-2">
                <div className="w-32">
                    <BlueButton text="Save" onClick={handleSave} loading={saving}/>
                </div>
            </div>
        </div>
    );
}

// --- List + edit page ---

export const SirenClientDriverConfigPage: React.FC = () => {
    const {clientID: contextClientID} = useSiren();
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const listRef = useRef<SelectListHandle>(null);

    useEffect(() => {
        if (!contextClientID || selectedClient) return;
        platformAPI.get<{id: string; name: string}>(`api/clients/${contextClientID}`)
            .then(res => setSelectedClient({id: res.data.id, name: res.data.name} as Client))
            .catch(() => {});
    }, [contextClientID]);

    const getClients = useCallback(async (search: string) => {
        const res = await platformAPI.get<{items: (Client & {sirenDriverConfig?: {callMode: DriverMode; emailMode: DriverMode; smsMode: DriverMode; appMode: DriverMode}})[]}>("api/clients", {
            params: {search, limit: 50, module: "siren", hydrate: "driverConfig"},
        });
        return (res.data.items ?? []).map(c => {
            const cfg = c.sirenDriverConfig;
            const callMode: DriverMode = cfg?.callMode ?? "disabled";
            const emailMode: DriverMode = cfg?.emailMode ?? "disabled";
            const smsMode: DriverMode = cfg?.smsMode ?? "disabled";
            const appMode: DriverMode = cfg?.appMode ?? "disabled";
            return {
                item: c,
                label: (
                    <span className="flex items-center gap-2 w-full min-w-0">
                        <span className="text-sm flex-1 min-w-0 truncate">{c.name}</span>
                        <ConfigDots callMode={callMode} emailMode={emailMode} smsMode={smsMode} appMode={appMode}/>
                    </span>
                ),
            };
        });
    }, []);

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <SelectList<Client>
                        ref={listRef}
                        getItems={getClients}
                        selectedID={selectedClient?.id ?? null}
                        onSelect={setSelectedClient}
                    />
                </div>

                <div className="flex-1 min-w-0 flex flex-col">
                    {selectedClient ? (
                        <ConfigPanel
                            key={selectedClient.id}
                            client={selectedClient}
                            onSaved={() => listRef.current?.refresh()}
                        />
                    ) : (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client to configure driver access.
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

// --- Standalone edit page (navigated to via URL) ---

export const SirenClientDriverConfigEditPage: React.FC = () => {
    const {clientID} = useParams<{clientID: string}>();
    const navigate = useNavigate();
    const [callMode, setCallMode] = useState<DriverMode>("disabled");
    const [emailMode, setEmailMode] = useState<DriverMode>("disabled");
    const [smsMode, setSmsMode] = useState<DriverMode>("disabled");
    const [appMode, setAppMode] = useState<DriverMode>("disabled");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!clientID) return;
        platformAPI.get<SirenClientDriverConfig>(`api/siren/client-driver-configs/${clientID}`)
            .then(res => {
                setCallMode(res.data.callMode);
                setEmailMode(res.data.emailMode);
                setSmsMode(res.data.smsMode);
                setAppMode(res.data.appMode ?? "disabled");
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [clientID]);

    const handleSave = useCallback(async () => {
        if (!clientID) return;
        setSaving(true);
        setError(null);
        try {
            await platformAPI.put(`api/siren/client-driver-configs/${clientID}`, {callMode, emailMode, smsMode, appMode});
            navigate("/siren/drivers/client-config");
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Error saving configuration");
        } finally {
            setSaving(false);
        }
    }, [clientID, callMode, emailMode, smsMode, appMode, navigate]);

    if (loading) return <Layout menuCurrent="config"><div className="p-4 text-sm text-light-grey">Loading…</div></Layout>;

    return (
        <Layout menuCurrent="config">
            <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-6 max-w-2xl">
                <p className="text-xs text-light-grey font-mono">{clientID}</p>
                <ModeSelector label="Call" value={callMode} onChange={setCallMode}/>
                <ModeSelector label="Email" value={emailMode} onChange={setEmailMode}/>
                <ModeSelector label="SMS" value={smsMode} onChange={setSmsMode}/>
                <ModeSelector label="App" value={appMode} onChange={setAppMode}/>
                {error && <p className="text-sm text-red">{error}</p>}
                <div className="flex gap-3 justify-end">
                    <button
                        className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey"
                        onClick={() => navigate("/siren/drivers/client-config")}
                    >
                        Cancel
                    </button>
                    <div className="w-32">
                        <BlueButton text="Save" onClick={handleSave} loading={saving}/>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
